/**
 * Shared team stats helpers used across FreeAgents, TeamPage, and Scouting.
 */

/**
 * Weekly fantasy points for one team in one week, or 0.
 *
 * weekly_points is keyed by the bare week number as a string — "3", never
 * "week3" and never "Week 3". The key is written by normalizeWeekKey in
 * backend/scoring.js, which strips non-digits out of whatever the config week
 * looks like. Four call sites had built the key by hand in two different wrong
 * shapes and silently read 0 forever, so build it here instead.
 *
 * Accepts anything week-shaped: 3, "3", "Week 3".
 */
export function getWeeklyPoints(currentSeason, week) {
  const n = parseInt(String(week ?? "").replace(/\D/g, ""), 10);
  if (isNaN(n)) return 0;
  return currentSeason?.weeklyPoints?.[String(n)] || 0;
}

/**
 * Returns total games played (wins + losses) from a "W-L" record string.
 * Used for calculating per-game averages.
 */
export function parseGamesPlayed(record) {
  if (!record || record === "0-0") return 0;
  const parts = record.split("-");
  if (parts.length !== 2) return 0;
  const wins = parseInt(parts[0]) || 0;
  const losses = parseInt(parts[1]) || 0;
  return wins + losses;
}

/**
 * Returns [wins, losses] from a "W-L" record string.
 * Used for record-based sorting.
 */
export function parseRecord(record) {
  if (!record || !record.includes("-")) return [0, 0];
  const [wins, losses] = record.split("-").map(Number);
  return [wins, losses];
}

/**
 * Safely calculates a per-game average, returning "0.0" when no games played.
 */
export function calculateAverage(total, gamesPlayed) {
  if (!gamesPlayed || gamesPlayed === 0) return "0.0";
  return (total / gamesPlayed).toFixed(1);
}
